import { supabase } from '../supabase';
import type { ReadDeps, SavedLayout, Proposed } from './readFile';
import type { LayoutProposal, LayoutSample } from './instructions';

/**
 * readFile's outside world in the browser: the reading library and Claude's
 * proposals, through Supabase (RLS) and two Netlify functions. Every call
 * fails soft — a lookup or proposal that cannot be made leaves the parser's
 * own reading to stand.
 */

const POLL_MS = 1500;

async function bearer(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const browserReadDeps: ReadDeps = {
  async aiEnabled() {
    const { data } = await supabase.from('app_config').select('value').eq('key', 'reading_ai_enabled').maybeSingle();
    return data?.value === 'true';
  },

  async lookup(layoutKey) {
    const { data } = await supabase
      .from('reading_layouts')
      .select('instructions')
      .eq('layout_key', layoutKey)
      .eq('status', 'active')
      .maybeSingle();
    const instructions = data?.instructions as SavedLayout | undefined;
    return instructions?.proposal && instructions?.sample ? instructions : null;
  },

  async propose(layoutKey: string, sample: LayoutSample, waitMs: number): Promise<Proposed | null> {
    const { data: row, error } = await supabase
      .from('layout_proposals')
      .insert({ layout_key: layoutKey, sample })
      .select('id')
      .single();
    if (error || !row) return null;
    void fetch('/.netlify/functions/layout-proposal-background', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ proposalId: row.id }),
    }).catch(() => {});

    const deadline = Date.now() + waitMs;
    while (Date.now() < deadline) {
      await sleep(POLL_MS);
      const { data } = await supabase.from('layout_proposals').select('status, proposal').eq('id', row.id).maybeSingle();
      if (!data || data.status === 'pending') continue;
      return data.status === 'done' && data.proposal ? { id: row.id, proposal: data.proposal as LayoutProposal } : null;
    }
    return null;
  },

  async save(proposalId, source, utility) {
    const token = await bearer();
    if (!token) return;
    await fetch('/.netlify/functions/reading-layouts', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'save', proposalId, source, utility }),
    });
  },

  async retire(layoutKey, reason) {
    const token = await bearer();
    if (!token) return;
    await fetch('/.netlify/functions/reading-layouts', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'retire', layoutKey, reason }),
    });
  },
};
